import { c as _c } from "react/compiler-runtime";
import type { EmblaCarouselType } from 'embla-carousel';
import emblaClassNames from 'embla-carousel-class-names';
import useEmblaCarousel from 'embla-carousel-react';
import { isNotNil } from 'es-toolkit';
import { useCallback, useEffect, useRef, useState } from 'react';
const TWEEN_FACTOR_BASE = 0.35;
const TWEEN_TRANSLATE_X = 50;
const TWEEN_TRANSLATE_Y = 10;
const GOLDEN_RATIO = 1 / 1.6180339887;
export const getInitialTweenTransform = (slideIndex: number) => {
  const scale = 1 - slideIndex * (1 - GOLDEN_RATIO);
  const translateX = -slideIndex * TWEEN_TRANSLATE_X;
  const translateY = slideIndex * TWEEN_TRANSLATE_Y;
  return `scale(${scale}) translate(${translateX}%, ${translateY}%)`;
};
export const useHeroCarousel = () => {
  const $ = _c(18);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      loop: false
    };
    t1 = [emblaClassNames({
      snapped: ["z-10", "!opacity-100"]
    })];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t0, t1);
  const tweenFactor = useRef(0);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const tweenNodes = useRef(t2);
  const [currentIndex, setCurrentIndex] = useState(0);
  let t3;
  if ($[3] !== emblaApi) {
    t3 = () => {
      if (emblaApi) {
        emblaApi.scrollPrev();
      }
    };
    $[3] = emblaApi;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const scrollPrev = t3;
  let t4;
  if ($[5] !== emblaApi) {
    t4 = () => {
      if (emblaApi) {
        emblaApi.scrollNext();
      }
    };
    $[5] = emblaApi;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const scrollNext = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = emblaApiInstance => {
      tweenNodes.current = emblaApiInstance.slideNodes().map(_temp).filter(isNotNil);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const setTweenNodes = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = emblaApiInstance_0 => {
      tweenFactor.current = TWEEN_FACTOR_BASE * emblaApiInstance_0.scrollSnapList().length;
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const setTweenFactor = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = emblaApiInstance_1 => {
      const engine = emblaApiInstance_1.internalEngine();
      const scrollProgress = emblaApiInstance_1.scrollProgress();
      const progressPerSlide = 1 / (emblaApiInstance_1.scrollSnapList().length - 1);
      emblaApiInstance_1.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        const diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];
        slidesInSnap.forEach(slideIndex => {
          const progress = diffToTarget / progressPerSlide;
          const scale = 1 - Math.abs(progress) * (1 - GOLDEN_RATIO);
          const translateX = -progress * TWEEN_TRANSLATE_X;
          const translateY = progress * TWEEN_TRANSLATE_Y;
          const tweenNode = tweenNodes.current[slideIndex];
          if (!tweenNode) {
            return;
          }
          tweenNode.style.transform = `scale(${scale}) translate(${translateX}%, ${translateY}%)`;
        });
      });
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const tweenScale = t7;
  let t8;
  let t9;
  if ($[10] !== emblaApi) {
    t8 = () => {
      if (!emblaApi) {
        return;
      }
      setTweenNodes(emblaApi);
      setTweenFactor(emblaApi);
      tweenScale(emblaApi);
      const onSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
      const onScrollOrFocus = api => tweenScale(api);
      const onReInit = () => {
        setTweenNodes(emblaApi);
        setTweenFactor(emblaApi);
        tweenScale(emblaApi);
      };
      emblaApi.on("reInit", onReInit).on("scroll", onScrollOrFocus).on("slideFocus", onScrollOrFocus).on("select", onSelect);
      return () => {
        emblaApi.off("reInit", onReInit).off("scroll", onScrollOrFocus).off("slideFocus", onScrollOrFocus).off("select", onSelect);
      };
    };
    t9 = [emblaApi, setTweenFactor, setTweenNodes, tweenScale];
    $[10] = emblaApi;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  if ($[13] !== currentIndex || $[14] !== emblaRef || $[15] !== scrollNext || $[16] !== scrollPrev) {
    t10 = {
      emblaRef,
      scrollPrev,
      scrollNext,
      currentIndex
    };
    $[13] = currentIndex;
    $[14] = emblaRef;
    $[15] = scrollNext;
    $[16] = scrollPrev;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
};
function _temp(slideNode) {
  return slideNode.querySelector(".embla__slide__image");
}